import * as React from 'react';
import { Box } from '@mui/system';
import { CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { GetTemplateEvent, GetTemplateResult } from 'bloggers-backend/lib/templates/domain/domain';
import { Template } from 'bloggers-backend/lib/templates/domain/Template';
import { AppContext } from '../../../../AppContext';
import { ArgFieldLabel, ArgFieldValue } from './ArgField';

export function TemplateArgValue({ templateId }: { templateId: string }) {
  const { client } = React.useContext(AppContext);

  const { data, isLoading } = useQuery<Template>(
    ['template', templateId],
    async () => {
      const result = await client<GetTemplateEvent, GetTemplateResult>({
        event: 'getTemplate',
        data: { id: templateId },
      });

      if (result.success) {
        return result.value;
      }

      throw new Error(result.reason);
    },
    { enabled: !!templateId, staleTime: Infinity, retry: false },
  );

  if (templateId && isLoading) {
    return <CircularProgress size={16} />;
  }

  if (!data) {
    return <ArgFieldValue>{templateId}</ArgFieldValue>;
  }

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.75,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
          {data.name}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(72px, max-content) 1fr',
          columnGap: 2,
          rowGap: 1.25,
          alignItems: 'baseline',
          px: 1.5,
          py: 1.25,
        }}
      >
        <ArgFieldLabel>Subject</ArgFieldLabel>
        <ArgFieldValue>{data.title}</ArgFieldValue>
        <ArgFieldLabel>Body</ArgFieldLabel>
        <ArgFieldValue>{data.text}</ArgFieldValue>
      </Box>
    </Box>
  );
}
